import React, { FC, useEffect } from 'react';
import Blade from '../Blade';
import { Kfh_BranchType } from '../../types/graphqlTypes';
import { Typography } from '../Typography';
import { IconLink } from '../IconLink';
import { BranchDivisionSummary } from './BranchDivisionDetails';
import { Divider } from '../Divider';
import { Spacer } from '../Spacer';
import HtmlContent from '../Html';
import { Img } from '../Img';
import { updateCallTracking } from '../../utils';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';

interface BranchBladeProps {
  branch: Kfh_BranchType;
  prioritiseLettings?: boolean;
  dynamic?: boolean;
}

export const BranchContent: FC<BranchBladeProps> = ({ branch, prioritiseLettings, dynamic }) => {
  const { dataLayerPush } = useDataLayerPush();
  const { name, businessProfile, googleMapLinkRental, googleMapLinkResales, openingHours, mediaSet } = branch;

  const salesContactDetails = businessProfile?.salesContactDetails;
  const lettingsContactDetails = businessProfile?.lettingsContactDetails;

  // Branch image
  const salesBranchImage = mediaSet?.media.find(({ role }) => role === 'Main')?.media;
  const lettingsBranchImage = mediaSet?.media.find(({ role }) => role === 'LettingsMain')?.media;

  useEffect(() => {
    updateCallTracking();

    dataLayerPush({
      event: 'blade action',
      bladeEvent: 'opened',
      bladeOpen: true,
      bladeEventID: `branch`,
      clickBladeId: `branch`,
      bladeBranchName: name,
      bladePropertyDetails: undefined,
    });
  }, []);

  return (
    <Spacer>
      {/* Heading and link */}

      <Spacer spacing="half">
        <Typography component="h2" variant="displaySmall">
          {branch.name}
        </Typography>
        <IconLink arrowRight url={branch.relativeUrl} label="View branch details page" />
      </Spacer>

      {/* Sales details */}

      {branch.isSales && (
        <>
          <BranchDivisionSummary
            contactDetails={salesContactDetails}
            type="sales"
            mapLink={googleMapLinkResales}
            name={name}
          />
          <Divider />
        </>
      )}

      {/* Lettings details */}

      {branch.isLettings && (
        <>
          <BranchDivisionSummary
            contactDetails={lettingsContactDetails}
            type="lettings"
            mapLink={googleMapLinkRental}
            name={name}
          />
          <Divider />
        </>
      )}

      {/* Opening Times */}

      <Spacer spacing="half">
        <Typography component="h2" variant="titleMedium">
          Opening times
        </Typography>
        {openingHours && <HtmlContent>{openingHours}</HtmlContent>}

        {/* Branch image */}

        {(salesBranchImage || lettingsBranchImage) && (
          <Img
            src={prioritiseLettings && lettingsBranchImage ? lettingsBranchImage : salesBranchImage}
            alt={prioritiseLettings && lettingsBranchImage ? 'lettings branch' : 'sales branch'}
            size="598x630"
          />
        )}
      </Spacer>
    </Spacer>
  );
};
export const BranchBlade: FC<BranchBladeProps> = ({ branch, prioritiseLettings, dynamic }) => (
  <Blade id="branch">
    {branch && <BranchContent branch={branch} prioritiseLettings={prioritiseLettings} dynamic={dynamic} />}
  </Blade>
);

export default BranchBlade;
